/**
 * App-shell met sidebar en topbar (design-handoff, scherm 3).
 *
 * De navigatie volgt de rol binnen het gekozen bedrijf (US-002/030) en toont onderin welke
 * omgeving draait, zodat staging nooit voor productie wordt aangezien (US-035).
 */

import { formatTimeNl, RELEASES, ROLE_LABELS, toLocalDateKey } from '@prijsmonitor/shared';
import { useEffect, useState } from 'react';
import { NavLink, Outlet, useLocation } from 'react-router';

import { api } from '../api/client.js';
import { Beeldmerk } from '../components/Beeldmerk.js';
import { useSession } from '../session/SessionContext.js';
import styles from './AppShell.module.css';
import { ProfielMenu } from './ProfielMenu.js';
import { titleForPath, visibleNavItems } from './navigation.js';

const ENVIRONMENT_LABELS: Record<string, string> = {
  production: 'Productie',
  staging: 'Staging',
  development: 'Ontwikkeling',
};

const ENVIRONMENT_DOTS: Record<string, string> = {
  production: styles.envProduction as string,
  staging: styles.envStaging as string,
  development: styles.envDevelopment as string,
};

export interface ShellContext {
  /** Titel die het actieve scherm in de topbar toont. */
  title: string;
}

/** De laatste controle, zoals hij bovenaan het scherm staat. */
interface LaatsteControle {
  startedAt: string | null;
  finishedAt: string | null;
  status: string | null;
}

/**
 * Zet de laatste controle om naar één regel: "vandaag 00:15 – 00:23".
 *
 * De dag wordt benoemd en niet als datum getoond zolang het vandaag of gisteren is; dat is hoe je
 * er zelf over praat, en het is precies het onderscheid dat telt. Is hij ouder, dan komt de datum
 * erbij — want dan is de vraag niet meer "hoe laat" maar "hoe lang geleden".
 */
function beschrijfControle(controle: LaatsteControle | null): string {
  if (!controle?.startedAt) return 'Nog geen controle uitgevoerd';

  const start = new Date(controle.startedAt);
  const vandaag = toLocalDateKey();
  const gisteren = toLocalDateKey(new Date(Date.now() - 24 * 60 * 60 * 1000));
  const dagsleutel = toLocalDateKey(start);

  const dag =
    dagsleutel === vandaag
      ? 'vandaag'
      : dagsleutel === gisteren
        ? 'gisteren'
        : new Date(controle.startedAt).toLocaleDateString('nl-NL', {
            weekday: 'short',
            day: '2-digit',
            month: '2-digit',
          });

  // Zonder eindtijd loopt hij nog; dat is iets anders dan een controle die is afgerond.
  const eind = controle.finishedAt ? formatTimeNl(new Date(controle.finishedAt)) : 'bezig…';
  return `Laatste controle: ${dag} ${formatTimeNl(start)} – ${eind}`;
}

export function AppShell(): React.JSX.Element {
  const { session, activeCompany, role } = useSession();
  const location = useLocation();

  const environment = session?.environment ?? 'development';
  const versie = session?.versie;
  const commit = session?.commit;
  // Een nummer dat (nog) niet in de lijst met officiële releases staat, is een ontwikkelversie:
  // staging draait bijvoorbeeld het beoogde nummer vóórdat de tag bestaat (VERSIONING.md §4).
  const isRelease = versie !== undefined && RELEASES.some((release) => release.versie === versie);
  const title = titleForPath(location.pathname);
  const items = visibleNavItems(role);

  /*
   * De laatste controle in de balk bovenaan, zoals in het ontwerp.
   *
   * Bij iedere paginawissel opnieuw ophalen: een controle die tijdens het werken afrondt hoort niet
   * pas na een herlading zichtbaar te worden. Het is één rij uit de database, dus dat kan.
   *
   * Stil bij een fout. Dit is aanvullende informatie naast het scherm; is er nog geen bedrijf
   * gekozen of gaat het verzoek mis, dan blijft de balk simpelweg leeg.
   */
  const [controle, setControle] = useState<LaatsteControle | null>(null);
  useEffect(() => {
    if (!activeCompany) return;
    api
      .get<LaatsteControle>('/api/laatste-controle')
      .then(setControle)
      .catch(() => undefined);
  }, [activeCompany, location.pathname]);

  return (
    <div className={styles.shell}>
      <aside className={styles.sidebar}>
        <div className={styles.brand}>
          <Beeldmerk width={26} alt="" />
          <div>
            <div className={styles.brandName}>Prijsmonitor</div>
            <div className={styles.brandOrg}>Schoonderwolf</div>
          </div>
        </div>

        <nav className={styles.nav} aria-label="Hoofdnavigatie">
          {items.map((item) => (
            <NavLink
              key={item.path}
              to={item.path}
              className={({ isActive }) =>
                (isActive ? styles.navItemActive : styles.navItem) as string
              }
            >
              <span>{item.label}</span>
            </NavLink>
          ))}
        </nav>

        <div className={styles.sidebarFooter}>
          <div className={styles.environment}>
            <span className={ENVIRONMENT_DOTS[environment]} aria-hidden="true" />
            {ENVIRONMENT_LABELS[environment] ?? environment}
          </div>
          <div className={styles.roleLine}>
            Rol: <strong>{role ? ROLE_LABELS[role] : 'onbekend'}</strong>
          </div>
          {/*
            Het versienummer van de code die nu draait. Naast de omgeving, want het is dezelfde
            vraag: wát staat hier eigenlijk voor me? Klikken opent de volledige geschiedenis.
          */}
          <NavLink
            className={styles.versieLink}
            to="/versies"
            title={
              isRelease ? 'Bekijk alle versies' : 'Nog geen officiële release: bekijk alle versies'
            }
          >
            {versie && versie !== 'onbekend'
              ? `${isRelease ? 'Versie' : 'Ontwikkelversie'} ${versie}`
              : 'Versies'}
          </NavLink>
          {/*
            De commit die draait. Het versienummer verandert per release; de commit zegt precies
            welke code hier staat, ook voor wijzigingen die nog geen versie hebben.
          */}
          {commit && commit !== 'onbekend' && (
            <div className={styles.commit} title="Commit van de code die nu draait">
              Commit {commit}
            </div>
          )}
        </div>
      </aside>

      <main className={styles.main}>
        {environment === 'staging' && (
          <div className={styles.stagingBanner} role="status">
            Stagingomgeving — testresultaten uit deze omgeving horen niet in productieoverzichten,
            historie of e-mails thuis.
          </div>
        )}

        <header className={styles.topbar}>
          <div className={styles.screenTitle}>{title ?? 'Prijsmonitor'}</div>
          <div className={styles.topbarRight}>
            {controle?.startedAt && (
              <span
                className={styles.laatsteControle}
                title="Wanneer de prijzen voor het laatst zijn gemeten"
              >
                {beschrijfControle(controle)}
              </span>
            )}
            {activeCompany &&
              (activeCompany.heeftLogo ? (
                /*
                  Het logo vervangt de naam, met de naam als alternatieve tekst. Bij meerdere
                  bedrijven is een logo sneller te herkennen dan tekst — en juist bij het wisselen
                  telt dat: je wilt in één oogopslag zien of je in de goede omgeving zit.
                */
                <img
                  className={styles.companyLogo}
                  src={`/api/bedrijven/${activeCompany.companyId}/logo`}
                  alt={activeCompany.companyName}
                  title={activeCompany.companyName}
                />
              ) : (
                <div className={styles.companyBadge}>{activeCompany.companyName}</div>
              ))}
            {/*
              De avatar was zélf de uitlogknop: je klikte op je profiel en stond buiten. Nu opent hij
              een menu met wie je bent, bij welk bedrijf je zit, de andere bedrijven en pas daarna
              Uitloggen.
            */}
            <ProfielMenu />
          </div>
        </header>

        {/*
          De sleutel op het gekozen bedrijf, en dat is geen truc maar de eis uit US-003: na een wissel
          mag er geen gegeven van het vorige bedrijf meer staan.

          Zonder deze sleutel ging het mis op precies één plek — het scherm waar je al stond. Wisselen
          gaat door naar het prijsoverzicht, en zat je daar al, dan verandert het adres niet, dus wordt
          er niets opnieuw opgebouwd en haalt geen enkel scherm zijn gegevens opnieuw op. Je zag dan het
          logo bovenaan wijzigen met de tabel van het vórige bedrijf eronder: het ergste soort fout,
          want het lijkt te werken.

          Dit hier en niet per scherm: elk scherm haalt zijn eigen gegevens op en houdt zijn eigen
          filters en selecties bij. Met een `activeCompany` in elke effect-afhankelijkheid zou één
          vergeten scherm dezelfde fout opleveren, en die filters zouden blijven staan. Een nieuwe
          sleutel bouwt alles opnieuw op, en dat is precies wat een wissel hoort te doen.
        */}
        <div className={styles.content} key={activeCompany?.companyId ?? 'geen-bedrijf'}>
          <Outlet />
        </div>
      </main>
    </div>
  );
}
